import { monthKeyFromDate } from './time';

const STORAGE_PREFIX_V1 = 'actions:v1:';
const STORAGE_PREFIX_V2 = 'actions:v2:';
const scopedActions = new Map();

const safeParse = (value, fallback) => {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch (e) {
    return fallback;
  }
};

const getScopeKey = (scopeKey) => (scopeKey ? String(scopeKey) : 'global');
const getKeyV1 = (monthKey) => `${STORAGE_PREFIX_V1}${monthKey}`;
const getDismissKeyV1 = (monthKey) => `${STORAGE_PREFIX_V1}dismissed:${monthKey}`;
const getReviewedKeyV1 = (monthKey) => `${STORAGE_PREFIX_V1}reviewed:${monthKey}`;
const getKeyV2 = (scopeKey, monthKey) => `${STORAGE_PREFIX_V2}${getScopeKey(scopeKey)}:${monthKey}`;
const getDismissKeyV2 = (scopeKey, monthKey) => `${STORAGE_PREFIX_V2}${getScopeKey(scopeKey)}:dismissed:${monthKey}`;
const getReviewedKeyV2 = (scopeKey, monthKey) => `${STORAGE_PREFIX_V2}${getScopeKey(scopeKey)}:reviewed:${monthKey}`;
const cloneActions = (actions = []) => actions.map((action) => ({ ...action }));
const isFullActionStorageKey = (key) => (
  key.startsWith(STORAGE_PREFIX_V2)
    && !key.includes(':dismissed:')
    && !key.includes(':reviewed:')
);

const clearLegacyFullActionStorage = (key) => {
  localStorage.removeItem(key);
};

const getDismissedIds = (monthKey, scopeKey) => {
  if (!monthKey) return new Set();
  const raw = localStorage.getItem(getDismissKeyV2(scopeKey, monthKey));
  const data = safeParse(raw, []);
  return new Set(Array.isArray(data) ? data : []);
};

const setDismissedIds = (monthKey, ids, scopeKey) => {
  if (!monthKey) return;
  localStorage.setItem(getDismissKeyV2(scopeKey, monthKey), JSON.stringify(Array.from(ids)));
};

const getReviewedIds = (monthKey, scopeKey) => {
  if (!monthKey) return new Set();
  const raw = localStorage.getItem(getReviewedKeyV2(scopeKey, monthKey));
  const data = safeParse(raw, []);
  return new Set(Array.isArray(data) ? data : []);
};

const setReviewedIds = (monthKey, ids, scopeKey) => {
  if (!monthKey) return;
  localStorage.setItem(getReviewedKeyV2(scopeKey, monthKey), JSON.stringify(Array.from(ids)));
};

const migrateV1ToV2 = (monthKey, scopeKey) => {
  const v1Key = getKeyV1(monthKey);
  const v1Raw = localStorage.getItem(v1Key);
  const v1Actions = safeParse(v1Raw, []);
  if (!Array.isArray(v1Actions) || v1Actions.length === 0) {
    localStorage.removeItem(v1Key);
    return [];
  }

  const v2Key = getKeyV2(scopeKey, monthKey);
  scopedActions.set(v2Key, cloneActions(v1Actions));

  const dismissedRaw = localStorage.getItem(getDismissKeyV1(monthKey));
  const reviewedRaw = localStorage.getItem(getReviewedKeyV1(monthKey));
  if (dismissedRaw) localStorage.setItem(getDismissKeyV2(scopeKey, monthKey), dismissedRaw);
  if (reviewedRaw) localStorage.setItem(getReviewedKeyV2(scopeKey, monthKey), reviewedRaw);

  localStorage.removeItem(v1Key);
  localStorage.removeItem(getDismissKeyV1(monthKey));
  localStorage.removeItem(getReviewedKeyV1(monthKey));
  return v1Actions;
};

export const getActions = (monthKey, scopeKey) => {
  if (!monthKey) return [];
  const actionKey = getKeyV2(scopeKey, monthKey);
  clearLegacyFullActionStorage(actionKey);
  const data = scopedActions.get(actionKey);
  if (Array.isArray(data) && data.length > 0) return cloneActions(data);
  return migrateV1ToV2(monthKey, scopeKey);
};

export const createBudgetAction = ({
  monthKey,
  categoryId,
  categoryName,
  statusKey,
  spentAmount,
  budgetAmount,
  occurredAt
}) => {
  const normalizedStatus = (() => {
    if (!statusKey) return 'slightly_over';
    const key = String(statusKey).toLowerCase().replace(/[\s_-]+/g, '_');
    if (key === 'exceeded' || key === 'exceeded_budget') return 'exceeded';
    if (key === 'slightly_over' || key === 'approaching' || key === 'approaching_limit') return 'slightly_over';
    return key;
  })();
  const title = categoryName
    ? `${categoryName} ${normalizedStatus === 'exceeded' ? 'exceeded budget' : 'is slightly over budget'}`
    : undefined;

  return {
    id: `budget:${monthKey}:${categoryId}:${normalizedStatus}`,
    monthKey,
    occurredAt,
    type: 'budget',
    categoryId,
    categoryName,
    status: normalizedStatus,
    spentAmount,
    budgetAmount,
    title
  };
};

export const upsertActions = (actions, scopeKey) => {
  const grouped = new Map();

  (actions || []).forEach((action) => {
    if (!action?.id) return;
    const key = action.monthKey || monthKeyFromDate(action.occurredAt);
    if (!key) return;
    const bucket = grouped.get(key) || [];
    bucket.push({ ...action, monthKey: action.monthKey || key });
    grouped.set(key, bucket);
  });

  const mergedByMonth = {};

  grouped.forEach((bucket, monthKey) => {
    const existing = getActions(monthKey, scopeKey);
    const dismissedIds = getDismissedIds(monthKey, scopeKey);
    const reviewedIds = getReviewedIds(monthKey, scopeKey);
    const byId = new Map(existing.map(action => [action.id, action]));

    bucket.forEach((action) => {
      const prev = byId.get(action.id);
      const dismissedAt = prev?.dismissedAt;
      const reviewedAt = prev?.reviewedAt;
      const isDismissed = dismissedAt || dismissedIds.has(action.id);
      const isReviewed = reviewedAt || reviewedIds.has(action.id);
      byId.set(action.id, {
        ...prev,
        ...action,
        dismissedAt: isDismissed ? (dismissedAt || action.dismissedAt || new Date().toISOString()) : action.dismissedAt,
        reviewedAt: isReviewed ? (reviewedAt || action.reviewedAt || new Date().toISOString()) : action.reviewedAt
      });
    });

    const merged = Array.from(byId.values());
    const actionKey = getKeyV2(scopeKey, monthKey);
    scopedActions.set(actionKey, cloneActions(merged));
    clearLegacyFullActionStorage(actionKey);
    mergedByMonth[monthKey] = merged;
  });

  return mergedByMonth;
};

export const dismissAction = (monthKey, actionId, scopeKey) => {
  if (!monthKey || !actionId) return [];
  const dismissedIds = getDismissedIds(monthKey, scopeKey);
  dismissedIds.add(actionId);
  setDismissedIds(monthKey, dismissedIds, scopeKey);
  const actions = getActions(monthKey, scopeKey).map(action =>
    action.id === actionId
      ? { ...action, dismissedAt: new Date().toISOString() }
      : action
  );
  const actionKey = getKeyV2(scopeKey, monthKey);
  scopedActions.set(actionKey, cloneActions(actions));
  clearLegacyFullActionStorage(actionKey);
  return actions;
};

export const reviewAction = (monthKey, actionId, scopeKey) => {
  if (!monthKey || !actionId) return [];
  const reviewedIds = getReviewedIds(monthKey, scopeKey);
  reviewedIds.add(actionId);
  setReviewedIds(monthKey, reviewedIds, scopeKey);
  const actions = getActions(monthKey, scopeKey).map(action =>
    action.id === actionId
      ? { ...action, reviewedAt: new Date().toISOString() }
      : action
  );
  const actionKey = getKeyV2(scopeKey, monthKey);
  scopedActions.set(actionKey, cloneActions(actions));
  clearLegacyFullActionStorage(actionKey);
  return actions;
};

export const clearActions = (monthKey, scopeKey) => {
  if (!monthKey) return;
  scopedActions.delete(getKeyV2(scopeKey, monthKey));
  localStorage.removeItem(getKeyV2(scopeKey, monthKey));
};

export const dedupeActions = (actions) => {
  const budgetByCategory = new Map();
  const importById = new Map();
  const others = [];

  (actions || []).forEach((action) => {
    if (!action?.id || !action.monthKey) return;
    if (action.type === 'budget' && action.categoryId) {
      const key = `${action.monthKey}:${action.categoryId}`;
      const existing = budgetByCategory.get(key);
      if (!existing || new Date(action.occurredAt) > new Date(existing.occurredAt)) {
        budgetByCategory.set(key, action);
      }
    } else if (action.type === 'import' && action.importId) {
      const key = `${action.monthKey}:${action.importId}`;
      importById.set(key, action);
    } else {
      others.push(action);
    }
  });

  return [
    ...Array.from(budgetByCategory.values()),
    ...Array.from(importById.values()),
    ...others
  ];
};

export const normalizeActionsForMonth = (monthKey, scopeKey) => {
  if (!monthKey) return [];
  const actions = getActions(monthKey, scopeKey);
  const keep = [];
  actions.forEach((action) => {
    const correctKey = action?.monthKey;
    if (!correctKey) {
      keep.push(action);
      return;
    }
    if (correctKey === monthKey) {
      keep.push(action);
    } else {
      const moved = { ...action, monthKey: correctKey };
      upsertActions([moved], scopeKey);
    }
  });
  const deduped = dedupeActions(keep);
  const actionKey = getKeyV2(scopeKey, monthKey);
  scopedActions.set(actionKey, cloneActions(deduped));
  clearLegacyFullActionStorage(actionKey);
  return deduped;
};

export const clearActionsForScope = (scopeKey) => {
  const prefix = `${STORAGE_PREFIX_V2}${getScopeKey(scopeKey)}:`;
  Array.from(scopedActions.keys()).forEach((key) => {
    if (key.startsWith(prefix)) {
      scopedActions.delete(key);
    }
  });
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(prefix)) {
      localStorage.removeItem(key);
    }
  });
};

export const clearLegacyActions = () => {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(STORAGE_PREFIX_V1) || isFullActionStorageKey(key)) {
      localStorage.removeItem(key);
    }
  });
};

export const clearAllEphemeralActions = () => {
  scopedActions.clear();
};
